import { useMemo, useEffect } from "react";
import { Box, makeStyles } from "@material-ui/core";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";

import { GraphSectionLayout } from "./GraphSectionLayout";
import { SolarReport } from "../../../../api/Quote/SolarReport";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { CustomChartTooltip } from "./CustomChartTooltip";
import { CustomChartTick } from "./CustomTickChart";
import { color, fontFamily } from "../../../../app/theme";
import { Toggle } from "../../../../components/Switch";
import { RootState } from "../../../../app/rootReducer";
import { fetchLeadReport, fetchSolarReport } from "../../../../store/QuoteSlice";
import { MAX_FINAL_COST, MIN_FINAL_COST } from "../../quoteDetail.constants";
import { EQuotePurchaserType } from "../../../../api/Types";
import { quoteVat, solarGrantAmountForLeadProduct } from "../../../../app/utils";
import { solarGrantAmountAvailable } from "../../../../app/utils";
import { Product } from "../../../../api/Product/Product";
import { useProducts } from "../../../../api/Product/ProductApi";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

const gradientOffset = (data?: { label: string; value: number }[]) => {
  if (!data) return 1;
  const dataMax = Math.max(...data.map((i) => i.value));
  const dataMin = Math.min(...data.map((i) => i.value));

  if (dataMax <= 0) {
    return 0;
  }
  if (dataMin >= 0) {
    return 1;
  }

  return dataMax / (dataMax - dataMin);
};

export const EstimateReturn = ({ solarReportData }: { solarReportData: SolarReport | null }) => {
  const { isMoreThanSm } = useBreakpoint();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { solarReportParam, quote, lead, selectedLeadProductId } = useSelector((state: RootState) => state.quote);

  const { data: products = [] as Product[] } = useProducts(selectedLeadProductId ? [selectedLeadProductId] : []);

  const leadProduct = products[0];

  const finalCost = useMemo(() => {
    if (quote) {
      return Number(quote?.price || 0) - solarGrantAmountAvailable(quote, solarReportData);
    } else if (leadProduct) {
      return leadProduct.prices[0].price - solarGrantAmountForLeadProduct(leadProduct);
    }
    return 0;
  }, [quote, leadProduct, solarReportData]);

  const initialOutlay = useMemo(() => {
    if (quote) {
      return (
        (quote?.purchaserType === EQuotePurchaserType.Home
          ? solarReportData?.netCost
          : (solarReportData?.netCost ?? 0) - quoteVat(quote)) ?? 0
      );
    } else if (leadProduct) {
      return solarReportData?.netCost ?? 0;
    }
    return 0;
  }, [quote, solarReportData, leadProduct]);

  const data = solarReportData?.savingsEstimateByYear.map((item, idx) => ({
    label: String(`0${idx + 1}`).slice(-2),
    value:
      item -
      (initialOutlay ?? 0) -
      (solarReportData?.financeRepaymentPerMonth ?? 0) *
        (Math.min(idx + 1, solarReportData.financeTermInYears ?? 0) * 12)
  }));

  const batteryEffect = useMemo(() => {
    const batteryData = {
      show: false,
      on: false
    };
    if (
      quote?.addons &&
      quote.addons.some(
        (addon) =>
          addon.classification?.toLowerCase().includes("battery") || addon.name?.toLowerCase().includes("battery")
      ) &&
      quote?.items &&
      !quote.items.some(
        (item) => item.classification?.toLowerCase().includes("battery") || item.name?.toLowerCase().includes("battery")
      )
    ) {
      batteryData.show = true;
    }

    if (lead) {
      batteryData.show = false;
    }

    if (
      quote?.addons &&
      quote.addons.some(
        (addon) =>
          (addon.classification?.toLowerCase().includes("battery") || addon.name?.toLowerCase().includes("battery")) &&
          addon.selected
      )
    ) {
      batteryData.on = true;
    }

    return batteryData;
  }, [quote, lead]);

  useEffect(() => {
    if (quote?.addons && quote.addons.some((addon) => addon.selected)) {
      dispatch(fetchSolarReport({ quoteId: quote?.id, includeBattery: true }));
    }
  }, [dispatch, quote]);

  return (
    <GraphSectionLayout
      title={"Estimated Return on Investment"}
      description={
        "This is an estimated graph showing the cost of your system and your savings over the lifetime of your system."
      }
      leftContent={
        <Box>
          <Box
            mt={isMoreThanSm ? "38px" : "23px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            {batteryEffect.show && (
              <Box display={"flex"} alignItems={"center"}>
                <Toggle
                  checked={solarReportParam.includeBattery}
                  onChange={(_e, checked) =>
                    quote && dispatch(fetchSolarReport({ quoteId: quote?.id, includeBattery: checked }))
                  }
                />{" "}
                <Box component={"span"} ml={"13px"} fontSize={isMoreThanSm ? "14px" : "12px"} color={"#4b5462"}>
                  with Battery
                </Box>
              </Box>
            )}
            {finalCost < MAX_FINAL_COST && finalCost > MIN_FINAL_COST ? (
              <Box display={"flex"} alignItems={"center"}>
                <Toggle
                  checked={solarReportParam.includeFinance}
                  onChange={(_e, checked) => {
                    if (quote) {
                      dispatch(fetchSolarReport({ quoteId: quote?.id, includeFinance: checked }));
                    }
                    if (lead) {
                      dispatch(
                        fetchLeadReport({ leadId: lead?.id, productId: selectedLeadProductId, includeFinance: checked })
                      );
                    }
                  }}
                />{" "}
                <Box component={"span"} ml={"13px"} fontSize={isMoreThanSm ? "14px" : "12px"} color={"#4b5462"}>
                  with Finance
                </Box>
              </Box>
            ) : null}
          </Box>
          <Box
            mt={"28px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            lineHeight={isMoreThanSm ? 1.71 : 2}
            fontWeight={500}
            letterSpacing={"0.32px"}
            color={"#4b5462"}
          >
            <Box>Total lifetime savings</Box>
            <Box>
              <PriceFormat
                value={
                  solarReportData?.savingsEstimateByYear?.[solarReportData?.savingsEstimateByYear?.length - 1] ?? 0
                }
              />
            </Box>
          </Box>
          <Box
            mt={"10px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            lineHeight={isMoreThanSm ? 1.71 : 2}
            fontWeight={500}
            letterSpacing={"0.32px"}
            color={"#4b5462"}
          >
            <Box>Initial outlay</Box>
            <Box color={initialOutlay > 0 ? color.orange : color.navy[100]}>
              <PriceFormat value={initialOutlay} prefix={initialOutlay > 0 ? "-€" : "€"} />
            </Box>
          </Box>
          {finalCost < MAX_FINAL_COST && finalCost > MIN_FINAL_COST ? (
            <Box
              mt={"10px"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              fontSize={isMoreThanSm ? "14px" : "12px"}
              lineHeight={isMoreThanSm ? 1.71 : 2}
              fontWeight={500}
              letterSpacing={"0.32px"}
              color={color.navy[100]}
            >
              <Box display={"flex"} alignItems={"center"}>
                Finance repayments{" "}
                <img
                  src="images/quote/logo-finance.png"
                  alt="ireland finance"
                  width={"58px"}
                  style={{ filter: "grayScale(100%)", marginLeft: "10px" }}
                />
              </Box>
              <Box color={(solarReportData?.financeRepaymentPerMonth ?? 0) > 0 ? color.orange : color.navy[100]}>
                <NumericFormat
                  value={
                    (solarReportData?.financeRepaymentPerMonth ?? 0) *
                      (solarReportData?.financeTermInYears ?? 0) *
                      12 || 0
                  }
                  displayType={"text"}
                  decimalScale={0}
                  fixedDecimalScale={true}
                  thousandSeparator={true}
                  prefix={(solarReportData?.financeRepaymentPerMonth ?? 0) > 0 ? "-€" : "€"}
                />
              </Box>
            </Box>
          ) : null}
          <Box
            mt={"15px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "16px" : "14px"}
            fontWeight={700}
            lineHeight={1.5}
            letterSpacing={"0.32px"}
            color={"#43335b"}
          >
            <Box>Net savings</Box>
            <Box>
              <PriceFormat
                value={
                  (solarReportData?.savingsEstimateByYear?.[solarReportData?.savingsEstimateByYear?.length - 1] ?? 0) -
                  (solarReportData?.financeRepaymentPerMonth ?? 0) * (solarReportData?.financeTermInYears ?? 0) * 12 -
                  (solarReportData?.netCost ?? 0)
                }
              />
            </Box>
          </Box>
        </Box>
      }
      rightContent={
        <Box width={"100%"} height={"330px"}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              width={500}
              height={400}
              data={data}
              margin={{
                top: isMoreThanSm ? 10 : 0,
                right: isMoreThanSm ? 30 : 0,
                left: 0,
                bottom: 0
              }}
              className={classes.chart}
              style={{ overflow: "inherit" }}
            >
              <CartesianGrid vertical={false} stroke="#efefef" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                style={{ fontSize: "12px", fontFamily: fontFamily.Axiforma }}
              />
              <YAxis axisLine={false} tickLine={false} tickCount={9} interval={0} tick={<CustomChartTick />} />
              <Tooltip
                content={<CustomChartTooltip />}
                wrapperStyle={{ outline: "none" }}
                cursor={{ stroke: "#a487ff" }}
              />
              <defs>
                <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={gradientOffset(data)} stopColor="#fbc0ad" stopOpacity={1} />
                  <stop offset={gradientOffset(data)} stopColor="#160337" stopOpacity={1} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke="none"
                activeDot={{ stroke: "#a487ff", fill: "#a487ff" }}
                fill="url(#splitColor)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      }
    />
  );
};
const useStyles = makeStyles((theme) => ({
  chart: {
    "& .recharts-surface": {
      overflow: "inherit"
    }
  },
  labelYear: {
    "& tspan": {
      dy: "-20px"
    }
  }
}));
